import React, { useState, useEffect, useContext } from 'react';
import { Tooltip } from 'react-tooltip';
import { useAppStore } from '../../../../../GlobalStore/GlobalStore.ts';
import SaveButton from '../Subcomponents/SaveButton/SaveButton.tsx';
import { UnsavedChangesContext, TabStateContext } from '../../Settings.tsx';
import { OverlayWrapper } from "../../../../OverlayWrapper.tsx";
import { writeSettings } from "../../../../../Utils/db/users/userSettings/writeSettings.ts";
import { isIde } from "../../../../../Utils/misc/envNameUtils.ts";
import CustomModesSection from "./CustomModesSection/CustomModesSection.tsx";
import CustomInstructionSection from "../Subcomponents/CustomInstructionSection/CustomInstructionSection.tsx";

interface CustomizationProps {}

const Customization: React.FC<CustomizationProps> = ({ }) => {
    const settingsObj = useAppStore((state: any) => state.settingsObj);
    const setSettingsObj = useAppStore((state: any) => state.setSettingsObj);
    const environmentObj = useAppStore((state: any) => state.environmentObj);
    const userObj = useAppStore((state: any) => state.userObj);

    const { customInstructions, customModes = {}, projectCustomInstructions = {} } = settingsObj;
    const { projectName, environment } = environmentObj;

    const { setTabHasUnsavedChanges } = useContext(UnsavedChangesContext);
    const { getTabState, setTabState } = useContext(TabStateContext);

    // Get persistent tab state or initialize with current values
    const persistentState = getTabState('Customization');

    // Get current project instructions (if exists)
    const currentProjectInstructions = (projectName && projectCustomInstructions[projectName]) || '';

    // Local state for tracking unsaved changes
    const [localCustomInstructions, setLocalCustomInstructions] = useState(persistentState.localCustomInstructions || customInstructions);
    const [localProjectCustomInstructions, setLocalProjectCustomInstructions] = useState(persistentState.localProjectCustomInstructions || currentProjectInstructions);
    const [localCustomModes, setLocalCustomModes] = useState(persistentState.localCustomModes || customModes);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(persistentState.hasUnsavedChanges || false);

    // Update local state when global state changes (only when not editing locally)
    useEffect(() => {
        if (!hasUnsavedChanges) {
            setLocalCustomInstructions(customInstructions || '');
        }
    }, [customInstructions, hasUnsavedChanges]);

    // Update local custom modes when global state changes (only when not editing locally)
    useEffect(() => {
        if (!hasUnsavedChanges) {
            setLocalCustomModes(customModes || {});
        }
    }, [customModes, hasUnsavedChanges]);

    // Update local project instructions when current project or global state changes (only when not editing locally)
    useEffect(() => {
        const updatedProjectInstructions = (projectName && projectCustomInstructions[projectName]) || '';
        if (!hasUnsavedChanges) {
            setLocalProjectCustomInstructions(updatedProjectInstructions);
        }
    }, [projectName, projectCustomInstructions, hasUnsavedChanges]);

    // Check if there are any actual changes
    const checkForChanges = (newInstructions: string, newProjectInstructions: string, newModes: any) => {
        const instructionsChanged = newInstructions !== (customInstructions || '');
        const projectInstructionsChanged = isIde(environment) && projectName && newProjectInstructions !== currentProjectInstructions;
        const modesChanged = JSON.stringify(newModes) !== JSON.stringify(customModes);
        return instructionsChanged || projectInstructionsChanged || modesChanged;
    };
    // Update custom instructions with revert detection
    const updateCustomInstructions = (event: any) => {
        const newText = event.target.value;
        setLocalCustomInstructions(newText);

        const hasActualChanges = checkForChanges(newText, localProjectCustomInstructions, localCustomModes);
        setHasUnsavedChanges(hasActualChanges);
    };

    // Update project custom instructions with revert detection
    const updateProjectCustomInstructions = (event: any) => {
        const newText = event.target.value;
        setLocalProjectCustomInstructions(newText);

        const hasActualChanges = checkForChanges(localCustomInstructions, newText, localCustomModes);
        setHasUnsavedChanges(hasActualChanges);
    };

    // Update custom modes from child component
    const updateCustomModes = (newModes: any) => {
        setLocalCustomModes(newModes);

        const hasActualChanges = checkForChanges(localCustomInstructions, localProjectCustomInstructions, newModes);
        setHasUnsavedChanges(hasActualChanges);
    };

    const handleSave = async () => {
        try {
            // Clear unsaved changes flag immediately to prevent confirmation popup if user closes quickly
            setHasUnsavedChanges(false);

            // Build updated project custom instructions object
            let updatedProjectCustomInstructions = { ...projectCustomInstructions };
            if (isIde(environment) && projectName) {
                updatedProjectCustomInstructions[projectName] = localProjectCustomInstructions;
            }

            // Update global state with custom instructions, project instructions, and modes
            setSettingsObj({
                customInstructions: localCustomInstructions,
                customModes: localCustomModes,
                projectCustomInstructions: updatedProjectCustomInstructions
            });

            // Save to persistent storage
            const settings: any = {
                customInstructions: localCustomInstructions,
                customModes: localCustomModes
            };

            // Only include project custom instructions if we're in IDE and have a project
            if (isIde(environment)) {
                settings.projectCustomInstructions = updatedProjectCustomInstructions;
            }

            await writeSettings(userObj.user, settings, environment);
        } catch (error) {
            console.error('Failed to save customization settings:', error);
            // If save fails, restore the unsaved changes flag
            setHasUnsavedChanges(true);
        }
    };
    // Report unsaved changes status to parent Settings component
    useEffect(() => {
        setTabHasUnsavedChanges('Customization', hasUnsavedChanges);
    }, [hasUnsavedChanges, setTabHasUnsavedChanges]);

    // Persist state changes to TabStateContext
    useEffect(() => {
        setTabState('Customization', {
            localCustomInstructions,
            localProjectCustomInstructions,
            localCustomModes,
            hasUnsavedChanges
        });
    }, [localCustomInstructions, localProjectCustomInstructions, localCustomModes, hasUnsavedChanges, setTabState]);

    return (
        <div className="dark:text-neutral-300 space-y-4 flex flex-col h-full">
            <div className="flex-1 space-y-4">
                <div className="space-y-3">
                    <CustomInstructionSection
                        label="Custom Instructions"
                        tooltipId="custom-instructions-tooltip"
                        tooltipContent={`Details or rules for the AI to always keep in mind.`}
                        value={localCustomInstructions}
                        onChange={(e) => updateCustomInstructions(e)}
                        maxLength={10000}
                    />
                    {isIde(environment) && projectName && (
                        <CustomInstructionSection
                            label={`Project Instructions (${projectName})`}
                            tooltipId="project-instructions-tooltip"
                            tooltipContent={`Project-specific details or rules for the AI to keep in mind when working on this project.`}
                            value={localProjectCustomInstructions}
                            onChange={(e) => updateProjectCustomInstructions(e)}
                            maxLength={10000}
                        />
                    )}
                </div>
                <div className="border-t border-neutral-600/30 pt-4">
                    <OverlayWrapper type="paidOnly">
                        <CustomModesSection
                            customModes={localCustomModes}
                            onCustomModesChange={updateCustomModes}
                        />
                    </OverlayWrapper>
                </div>
            </div>

            <SaveButton
                hasUnsavedChanges={hasUnsavedChanges}
                onSave={handleSave}
            />

            <Tooltip id={`custom-instructions-tooltip`} />
            <Tooltip id={`project-instructions-tooltip`} />
            <Tooltip id={`custom-modes-tooltip`} />
        </div>
    );
};

export default Customization;